import { onUnmounted, type Ref, type ComputedRef } from 'vue';

export interface GifHandle {
  toggle(): void;
  pause(): void;
  seekTo(frame: number): void;
  currentFrame: { value: number };
  frameCount: { value: number };
}

/** Approximate frame duration for video frame-step (assumes ~30 fps). */
const FRAME_DURATION = 1 / 30;

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    (el as HTMLElement).isContentEditable
  );
}

/**
 * Keyboard shortcuts for video and GIF media playback.
 *
 * | Key       | Video                               | GIF                        |
 * |-----------|-------------------------------------|----------------------------|
 * | ⎵         | toggle play/pause                   | toggle play/pause          |
 * | ,         | pause + step one frame back (~30fps)| pause + seek frame back    |
 * | .         | pause + step one frame forward      | pause + seek frame forward |
 * | ⇧←        | seek back 1 s (< 30 s) or 5 s      | —                          |
 * | ⇧→        | seek forward 1 s (< 30 s) or 5 s   | —                          |
 *
 * Bindings are suppressed when `isActive` is false or a text input is focused.
 *
 * @param videoRef  Ref to the `<video>` element (or null when none).
 * @param gifRef    Ref to the exposed GifControls interface (or null when none).
 * @param isActive  Whether this slide is the currently focused one.
 * @param isVideo   Whether the current media is a native video.
 * @param isGif     Whether the current media is a GIF.
 */
export function useMediaHotkeys(
  videoRef: Ref<HTMLVideoElement | null>,
  gifRef: Ref<GifHandle | null>,
  isActive: Ref<boolean> | ComputedRef<boolean>,
  isVideo: Ref<boolean> | ComputedRef<boolean>,
  isGif: Ref<boolean> | ComputedRef<boolean>,
): void {
  function onKeydown(e: KeyboardEvent) {
    if (!isActive.value || isInputFocused()) return;

    const video = videoRef.value;
    const gif = gifRef.value;

    if (isVideo.value && video) {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          video.paused ? video.play() : video.pause();
          break;
        case ',':
          e.preventDefault();
          video.pause();
          video.currentTime = Math.max(0, video.currentTime - FRAME_DURATION);
          break;
        case '.':
          e.preventDefault();
          video.pause();
          video.currentTime = Math.min(video.duration || 0, video.currentTime + FRAME_DURATION);
          break;
        case 'ArrowLeft':
          if (!e.shiftKey) return;
          e.preventDefault();
          video.currentTime = Math.max(0, video.currentTime - (video.duration < 30 ? 1 : 5));
          break;
        case 'ArrowRight':
          if (!e.shiftKey) return;
          e.preventDefault();
          video.currentTime = Math.min(
            video.duration || 0,
            video.currentTime + (video.duration < 30 ? 1 : 5),
          );
          break;
      }
    } else if (isGif.value && gif) {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          gif.toggle();
          break;
        case ',':
          e.preventDefault();
          gif.pause();
          gif.seekTo(gif.currentFrame.value - 1);
          break;
        case '.':
          e.preventDefault();
          gif.pause();
          gif.seekTo(gif.currentFrame.value + 1);
          break;
      }
    }
  }

  window.addEventListener('keydown', onKeydown);
  onUnmounted(() => window.removeEventListener('keydown', onKeydown));
}
